import type { RouteObject } from "react-router-dom-7"
import { compose } from "underscore"

import { getUserInfo, withRestrictedAccess } from "~/domain/user-session"
import { withSuspense } from "~/shared/ui/hocs"
import { withErrorBoundary } from "~/shared/ui"

export const routes = [
	{ path: "personal-area", lazy: () => import("./pages/personal-area") },

	{
		path: "personal-area/teacher/rating",
		lazy: () => import("./pages/personal-area/teacher/rating"),
		restrictedAccessProps: { userType: "сотрудник" }
	},
	{ path: "personal-area/students/rating", lazy: () => import("./pages/personal-area/student/rating") },
	{
		path: "personal-area/teacher/schedule",
		lazy: () => import("./pages/personal-area/teacher/schedule"),
		restrictedAccessProps: { userType: "сотрудник" },
		children: [
			{ index: true, lazy: () => import("./pages/personal-area/schedule/teachers") },
			{ path: "auditory", lazy: () => import("./pages/personal-area/schedule/auditory") },
			{ path: "groups", lazy: () => import("./pages/personal-area/schedule/groups") }
		]
	},
	{
		path: "personal-area/student/schedule",
		lazy: () => import("./pages/personal-area/student/schedule"),
		children: [
			{ index: true, lazy: () => import("./pages/personal-area/student/schedule/group") },
			{ path: "teachers", lazy: () => import("./pages/personal-area/schedule/teachers") },
			{ path: "groups", lazy: () => import("./pages/personal-area/schedule/groups") }
		]
	},
	{
		path: "employee-area",
		lazy: () => import("./pages/employee-area"),
		restrictedAccessProps: { userType: "сотрудник" }
	},
	{ path: "admin-area", lazy: () => import("./pages/admin-area"), restrictedAccessProps: { userType: "сотрудник" } },
	{
		path: "admin-area/docs",
		lazy: () => import("./pages/admin-area/docs"),
		restrictedAccessProps: {
			userType: "сотрудник",
			objects: "docs:read"
		}
	},
	{
		path: "admin-area/integration",
		lazy: () => import("./pages/admin-area/integration"),
		restrictedAccessProps: {
			userType: "сотрудник",
			objects: "eios:admin"
		}
	},
	{
		path: "admin-area/logs",
		lazy: () => import("./pages/admin-area/logs"),
		restrictedAccessProps: {
			userType: "сотрудник",
			objects: "eios:admin"
		}
	},
	{
		path: "admin-area/auth-logs",
		lazy: () => import("./pages/admin-area/auth-logs"),
		restrictedAccessProps: {
			userType: "сотрудник",
			objects: "eios:admin"
		}
	},
	{ path: "manuals", lazy: () => import("./pages/manuals"), publicPage: true },
	{
		path: "registration-application",
		//lazy: () => import("./pages/registration-application"),
		lazy: () => import("./pages/registration-application/gen-form"),
		publicPage: true
	},
	{
		path: "registration-application/gen-form",
		lazy: () => import("./pages/registration-application/gen-form"),
		publicPage: true
	},
	{ path: "add-applications", lazy: () => import("./pages/add-applications"), publicPage: true },
	{
		path: "institute-contacts",
		lazy: () => import("./pages/institute-contacts"),
		publicPage: true
	},
	{ path: "personal-area/student/personal-data", lazy: () => import("./pages/personal-area/student/personal-data") },
	{ path: "personal-area/student/study-cards", lazy: () => import("./pages/personal-area/student/study-cards") },
	{ path: "personal-area/student/study-plan", lazy: () => import("./pages/personal-area/student/study-plan") },
	{ path: "personal-area/student/debt", lazy: () => import("./pages/personal-area/student/debt") },
	{ path: "personal-area/student/dates", lazy: () => import("./pages/personal-area/student/dates") },
	{ path: "personal-area/student/study-ref", lazy: () => import("./pages/personal-area/student/study-ref") },
	{ path: "personal-area/student/portfolio", lazy: () => import("./pages/personal-area/student/portfolio") },
	{
		path: "personal-area/student/portfolio/infoupro",
		lazy: () => import("./pages/personal-area/student/portfolio/infoupro")
	},
	{
		path: "personal-area/student/portfolio/infoupro/:courseId/task-blocks",
		lazy: () => import("./pages/personal-area/student/portfolio/infoupro/task-blocks")
	},
	{
		path: "personal-area/student/portfolio/infoupro/:courseId/task-blocks/:taskBlockId/tasks",
		lazy: () => import("./pages/personal-area/student/portfolio/infoupro/task-blocks/tasks")
	},
	{
		path: "personal-area/student/portfolio/brs/:studyCardId",
		lazy: () => import("./pages/personal-area/student/portfolio/brs")
	},
	{
		path: "personal-area/student/portfolio/brs/:studyCardId/disc-rating",
		lazy: () => import("./pages/personal-area/student/portfolio/brs/disc-rating")
	},
	{
		path: "personal-area/student/portfolio/brs/:studyCardId/disc-rating/:studyId/activities",
		lazy: () => import("./pages/personal-area/student/portfolio/brs/disc-rating/activities")
	},
	{
		path: "personal-area/student/portfolio/extra/:studyCardId",
		lazy: () => import("./pages/personal-area/student/portfolio/extra")
	},
	{
		path: "personal-area/student/portfolio/extra/:studyCardId/achievements",
		lazy: () => import("./pages/personal-area/student/portfolio/extra/achievements")
	},
	{ path: "personal-area/student/bypass-sheet", lazy: () => import("./pages/personal-area/student/bypass-sheet") },
	{ path: "personal-area/student/group-leader", lazy: () => import("./pages/personal-area/student/group-leader") },
	{
		path: "personal-area/student/income-statement",
		lazy: () => import("./pages/personal-area/student/income-statement")
	},
	{
		path: "personal-area/student/foreign-stud-app-form",
		lazy: () => import("./pages/personal-area/student/foreign-stud-app-form")
	},
	{ path: "messages", lazy: () => import("./pages/messages") },
	{
		path: "profile",
		lazy: () => import("./pages/profile"),
		restrictedAccessProps: {
			ignoreNotChangedPassword: true
		},
		children: [
			{ path: "personal-data", lazy: () => import("./pages/profile/personal-data") },
			{ path: "auth-history", lazy: () => import("./pages/profile/auth-history") },
			{ path: "access-groups", lazy: () => import("./pages/profile/access-groups") },
			{ path: "push-notification", lazy: () => import("./pages/profile/push-notification") },
			{ path: "user-sessions", lazy: () => import("./pages/profile/user-sessions") },
			{ path: "change-password", lazy: () => import("./pages/profile/change-password") }
		]
	},
	{ path: "notifications", lazy: () => import("./pages/notifications") },
	{ path: "restore-password", lazy: () => import("./pages/restore-password"), publicPage: true }
].map(({ path, lazy, restrictedAccessProps = {}, publicPage, children }) => ({
	path,
	children,
	lazy: async () => {
		const module = await lazy()
		const Component = module.Component

		if (path.startsWith("personal-area/student/")) {
			(restrictedAccessProps as any).userType = "обучающийся"
		}

		if (path.startsWith("personal-area/teacher/rating") && getUserInfo()?.userType === "обучающийся") {
			location.assign("/main/personal-area")
		}

		return {
			Component: publicPage
				? compose(
					withSuspense(),
					withErrorBoundary()
				)(Component)
				: compose(
					withSuspense(),
					withErrorBoundary(),
					withRestrictedAccess(restrictedAccessProps as any)
				)(Component)
		}
	}
})) as RouteObject[]
